import type {
  AdminClientsListFilters,
  AdminClientsListRow,
  AdminClientsListSort,
  TriState,
} from './types';

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

export function rowMatchesSearch(row: AdminClientsListRow, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase().trim();
  const parts = [
    row.displayName,
    row.legalFirstName,
    row.legalLastName,
    row.preferredFirstName,
    row.preferredLastName,
    row.email,
    row.phone,
    row.sourceLabel,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return parts.includes(q) || parts.split(/\s+/).some((w) => w.startsWith(q));
}

function matchesTriState(value: boolean, filter: TriState): boolean {
  if (filter === 'any') return true;
  if (filter === 'yes') return value;
  return !value;
}

/** Empty filter = no filter. Non-empty compares digit-only strings (exact date from picker works). */
export function matchesDobFilter(row: AdminClientsListRow, dobFilter: string): boolean {
  const t = dobFilter.trim();
  if (!t) return true;
  if (!row.dateOfBirth) return false;
  const fd = digitsOnly(t);
  const rd = digitsOnly(row.dateOfBirth);
  if (fd.length === 0) return true;
  return rd.includes(fd) || fd.includes(rd);
}

function compareName(a: AdminClientsListRow, b: AdminClientsListRow): number {
  return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
}

function compareDob(a: AdminClientsListRow, b: AdminClientsListRow): number {
  const ad = a.dateOfBirth ? new Date(a.dateOfBirth).getTime() : 0;
  const bd = b.dateOfBirth ? new Date(b.dateOfBirth).getTime() : 0;
  if (!a.dateOfBirth && !b.dateOfBirth) return compareName(a, b);
  if (!a.dateOfBirth) return 1;
  if (!b.dateOfBirth) return -1;
  return ad - bd;
}

function compareType(a: AdminClientsListRow, b: AdminClientsListRow): number {
  const order = (t: string) => (t === 'client' ? 0 : 1);
  const c = order(a.recordType) - order(b.recordType);
  return c !== 0 ? c : compareName(a, b);
}

function sortRows(rows: AdminClientsListRow[], sort: AdminClientsListSort): AdminClientsListRow[] {
  const mult = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    let cmp = 0;
    if (sort.column === 'name') cmp = compareName(a, b);
    else if (sort.column === 'dob') cmp = compareDob(a, b);
    else cmp = compareType(a, b);
    if (cmp !== 0) return mult * cmp;
    return a.id.localeCompare(b.id);
  });
}

export function filterAdminClientsListRows(
  rows: AdminClientsListRow[],
  filters: AdminClientsListFilters,
  sort: AdminClientsListSort
): AdminClientsListRow[] {
  let out = rows;

  if (filters.tab === 'clients') {
    out = out.filter((r) => r.recordType === 'client');
  } else if (filters.tab === 'contacts') {
    out = out.filter((r) => r.recordType === 'contact');
  }

  if (filters.search.trim()) {
    out = out.filter((r) => rowMatchesSearch(r, filters.search));
  }

  out = out.filter((r) => matchesTriState(r.isGuarantor, filters.guarantor));
  out = out.filter((r) => matchesTriState(r.isSubscriber, filters.subscriber));
  out = out.filter((r) => matchesDobFilter(r, filters.dateOfBirth));

  return sortRows(out, sort);
}
